"use client";

import Image, { ImageProps, StaticImageData } from "next/image";
import { ReactEventHandler, useEffect, useRef, useState } from "react";

import NoImage from "@/assets/images/no_image.svg";
import { cn } from "@/lib/tailwind";
import { getCloudinaryURL } from "@/utils/cloudinary";

const LazyImage = ({
  src,
  onLoad,
  onError,
  className,
  showFallback = true,
  ...restProps
}: Omit<ImageProps, "className"> & {
  showFallback?: boolean;
  className?: string | ((isLoading: boolean, isLoadingError: boolean) => string);
}) => {
  const timeoutRef = useRef<NodeJS.Timeout>();

  const [isImageLoading, setIsImageLoading] = useState(true);
  const [isLoadingError, setIsLoadingError] = useState(false);
  const [url, setUrl] = useState<string | StaticImageData>(
    src && typeof src === "string" ? getCloudinaryURL(src) || NoImage : src || NoImage,
  );

  const handleError: ReactEventHandler<HTMLImageElement> = (e) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setUrl(NoImage);
    setIsLoadingError(true);
    setIsImageLoading(false);
    onError?.(e);
  };

  const handleOnLoad: ReactEventHandler<HTMLImageElement> = (e) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsImageLoading(false);
    setIsLoadingError(typeof url === "string" ? url === NoImage.src : url.src === NoImage.src);
    onLoad?.(e);
  };

  useEffect(() => {
    timeoutRef.current = setTimeout(() => setIsImageLoading(false), 8000);
    return () => clearTimeout(timeoutRef.current);
  }, []);

  useEffect(() => {
    setUrl(src && typeof src === "string" ? getCloudinaryURL(src) || NoImage : src || NoImage);
  }, [src]);

  return (
    <Image
      placeholder="blur"
      onLoad={handleOnLoad}
      onError={handleError}
      blurDataURL={NoImage.src}
      src={showFallback ? url : src}
      className={cn(
        "transition-[filter] duration-300 ease-in",
        isImageLoading ? "blur-[8px]" : "blur-none",
        className &&
          (typeof className === "string" ? className : className(isImageLoading, isLoadingError)),
      )}
      {...restProps}
    />
  );
};

export default LazyImage;
